import { FormFieldType } from '../components/FormFieldType';
import { GenericFieldProps } from '../components/GenericFormField';

type InitialValues<T> = {
  [K in keyof T]: T[K] extends { initialValue: infer V }
    ? V
    : T[K] extends { config: infer C }
    ? InitialValues<C>
    : never;
};

/**
 * Generates initial values for Formik forms from a fields configuration, supporting nested fields.
 * It handles undefined configurations by returning an empty object and recurses into nested
 * configurations to construct a hierarchical structure of initial values.
 *
 * @param fieldsConfig - Optional configuration object for form fields, specifying initial values
 *                       and potentially nested configurations.
 * @returns An object mapping field names to their initial values, including nested structures.
 *
 * Example:
 * const formFieldsConfig = {
 *   simpleField: { initialValue: 'simple' },
 *   nestedField: {
 *     type: FormFieldType.Nested,
 *     config: { nestedSimpleField: { initialValue: 'nestedSimple' } }
 *   }
 * };
 * const initialValues = generateCreateInitialValues(formFieldsConfig);
 * // Result: { simpleField: 'simple', nestedField: { nestedSimpleField: 'nestedSimple' } }
 */
export function generateCreateInitialValues<
  T extends Record<string, GenericFieldProps>
>(fieldsConfig: T | undefined): InitialValues<T> {
  if (!fieldsConfig) {
    return {} as InitialValues<T>;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const initialValues: any = {};
  Object.entries(fieldsConfig).forEach(([key, config]) => {
    if ('initialValue' in config) {
      initialValues[key] = config.initialValue;
    } else if (config.type === FormFieldType.Nested && 'config' in config) {
      initialValues[key] = generateCreateInitialValues(config.config);
    }
  });

  return initialValues;
}
